import { toast } from "@/v2-ui/toast"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import { useTranslationDeleteMutation } from "@/v2-backoffice-client/translation/orphaned/translation.delete.mutation"
import { useTranslationDeleteTypeValuesMutation } from "@/v2-backoffice-client/translation/deleteRows/translation.deleteTypeValues.mutation"
import type { TranslationRow } from "@/v2-backoffice-client/translation/translation.types"

export type TranslationDeleteRowsActionProps = {
  selectedRows: TranslationRow[]
  onComplete?(): void
}

type ActionResult = { Code: string; Ok: boolean; Error?: string | null }

// Selection-toolbar action for the catalog tables: soft-delete the selected
// rows from the translation database, OR restore them, depending on what's
// selected. The selection can mix both row kinds, so it is split by
// objectType — features go through setFeaturesDeleted, data labels through
// setTypeValuesDeleted. Soft delete keeps the temporal history, which is what
// makes restore possible.
//
// Restore mode activates only when EVERY selected row is already deleted —
// the catalog's Status filter shows one bucket (Active/Deleted/All) at a
// time, so a real mixed selection only happens under "All", where defaulting
// to Delete (rather than silently restoring active rows too) is the safer
// read of an ambiguous selection.
function TranslationDeleteRowsAction(props: TranslationDeleteRowsActionProps) {
  const { selectedRows, onComplete } = props

  const [removeFeatures, removeFeaturesState] = useTranslationDeleteMutation()
  const [removeTypeValues, removeTypeValuesState] =
    useTranslationDeleteTypeValuesMutation()

  const isRestoring =
    selectedRows.length > 0 && selectedRows.every((row) => row.isDeleted)

  const featureCodes = selectedRows
    .filter((row) => row.objectType === "Feature")
    .map((row) => row.code)
  const typeValueKeys = selectedRows
    .filter((row) => row.objectType === "TypeValue")
    .map((row) => row.code)
  const total = featureCodes.length + typeValueKeys.length
  const loading = removeFeaturesState.loading || removeTypeValuesState.loading

  async function onConfirm() {
    const actionVerb = isRestoring ? "Restored" : "Deleted"
    try {
      const results: ActionResult[] = []
      // errorPolicy "all" resolves instead of throwing on GraphQL errors, so
      // surface them explicitly — otherwise a failed batch reads as
      // "Deleted 0 row(s)".
      if (featureCodes.length > 0) {
        const res = await removeFeatures({
          variables: { codes: featureCodes, isDeleted: !isRestoring },
        })
        if (res.errors?.length) throw new Error(res.errors[0].message)
        results.push(...(res.data?.setFeaturesDeleted ?? []))
      }
      if (typeValueKeys.length > 0) {
        const res = await removeTypeValues({
          variables: { objectKeys: typeValueKeys, isDeleted: !isRestoring },
        })
        if (res.errors?.length) throw new Error(res.errors[0].message)
        results.push(...(res.data?.setTypeValuesDeleted ?? []))
      }

      const failed = results.filter((result) => !result.Ok)
      if (failed.length === 0) {
        toast({
          title: `${actionVerb} ${results.length} row(s)${isRestoring ? "" : " (soft delete)"}.`,
        })
      } else {
        toast({
          variant: "destructive",
          title: `${actionVerb} ${results.length - failed.length} row(s), ${failed.length} failed`,
          description: `${failed[0].Code}: ${failed[0].Error ?? "unknown error"}`,
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: isRestoring ? "Restore failed" : "Delete failed",
        description: error instanceof Error ? error.message : String(error),
      })
    }
    onComplete?.()
  }

  if (isRestoring) {
    return (
      <ConfirmButton
        variant="success"
        isStacked
        iconSrc="trash-restore"
        disabled={loading}
        isLoading={loading}
        content="Restore the selected rows — they resume publishing and reappear on every catalog surface."
        confirmContent={`Restore ${total} row(s)? They'll resume publishing and reappear on every catalog surface.`}
        confirmVariant="success"
        onConfirm={onConfirm}
        data-feature-code="UI_BackofficeTranslation_Catalog_Restore"
      >
        Restore
      </ConfirmButton>
    )
  }

  return (
    <ConfirmButton
      variant="destructive"
      isStacked
      iconSrc="trash"
      disabled={loading || total === 0}
      isLoading={loading}
      content="Soft-delete the selected rows from the translation database. History is kept and a row can be restored, but the texts stop being published."
      confirmContent={`Delete ${total} row(s) from the translation database? This is a soft delete — history is kept.`}
      confirmVariant="destructive"
      onConfirm={onConfirm}
      data-feature-code="UI_BackofficeTranslation_Catalog_Delete"
    >
      Delete
    </ConfirmButton>
  )
}

export default TranslationDeleteRowsAction
